import { Battery, Zap } from 'lucide-react';
import { motion } from 'motion/react';
import {
  NAV_TABS_LEFT,
  NAV_TABS_RIGHT,
  NavScanButton,
  NavTabButton,
  type NavTab,
} from './BottomNav';
import { useNotificationStore } from '../../store/notificationStore';

interface BottomNavChargingProps {
  activeTab: NavTab;
  onTabChange: (tab: NavTab) => void;
  onScan: () => void;
  hasActiveSession?: boolean;
  // Charging session data
  batteryLevel?: number;
  energyConsumed?: number;
  onChargingClick?: () => void;
}

export function BottomNavCharging({
  activeTab,
  onTabChange,
  onScan,
  hasActiveSession = false,
  batteryLevel = 0,
  energyConsumed = 0,
  onChargingClick,
}: BottomNavChargingProps) {
  const unreadCount = useNotificationStore((s) => s.unreadCount);

  // Same five slots as the idle bar, tinted for whichever bar they sit in — so
  // starting a session adds the status strip above without the scan button or
  // the tabs moving underneath.
  const renderSlots = (tone: 'default' | 'charging') => (
    <>
      {NAV_TABS_LEFT.map((tab) => (
        <NavTabButton
          key={tab.id}
          tab={tab}
          tone={tone}
          isActive={activeTab === tab.id}
          onClick={() => onTabChange(tab.id)}
        />
      ))}

      <NavScanButton onClick={onScan} tone={tone} />

      {NAV_TABS_RIGHT.map((tab) => (
        <NavTabButton
          key={tab.id}
          tab={tab}
          tone={tone}
          isActive={activeTab === tab.id || (tab.id === 'profile' && activeTab === 'notifications')}
          onClick={() => onTabChange(tab.id)}
          badge={tab.id === 'profile' ? unreadCount : 0}
        />
      ))}
    </>
  );

  if (!hasActiveSession) {
    // Regular bottom nav when not charging
    return (
      <nav className="bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 shadow-[0_-4px_20px_rgba(0,0,0,0.08)] dark:shadow-[0_-4px_20px_rgba(0,0,0,0.3)] flex-shrink-0 transition-colors relative z-[1000] pb-[env(safe-area-inset-bottom)]">
        <div className="flex items-stretch justify-around px-2 py-2">{renderSlots('default')}</div>
      </nav>
    );
  }

  // Transformed charging mode
  return (
    <motion.nav
      initial={{ backgroundColor: 'rgb(255, 255, 255)' }}
      animate={{
        background: 'linear-gradient(135deg, #8bc43e 0%, #6fa02e 100%)',
      }}
      transition={{ duration: 0.5 }}
      className="shadow-[0_-4px_30px_rgba(139,196,62,0.3)] flex-shrink-0 relative z-[1000] pb-[env(safe-area-inset-bottom)]"
    >
      {/*
        The sweeping glow is clipped to its own layer rather than to the nav:
        the raised scan button breaks above the bar's top edge, and an
        overflow-hidden nav would slice the top off it.
      */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
          animate={{
            x: ['-100%', '200%'],
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
            ease: 'linear',
          }}
        />
      </div>

      {/* Charging info bar */}
      <motion.div
        initial={{ height: 0, opacity: 0 }}
        animate={{ height: 'auto', opacity: 1 }}
        className="relative border-b border-white/20 px-4 py-2"
      >
        <button
          onClick={onChargingClick}
          className="w-full flex items-center gap-3 hover:bg-white/10 rounded-lg px-2 py-1 transition-colors active:scale-[0.98]"
        >
          {/* Pulsing charging icon */}
          <motion.div
            className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center"
            animate={{
              scale: [1, 1.1, 1],
              boxShadow: [
                '0 0 0 0 rgba(255,255,255,0.4)',
                '0 0 0 8px rgba(255,255,255,0)',
                '0 0 0 0 rgba(255,255,255,0)',
              ],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
            }}
          >
            <Zap className="w-4 h-4 text-white fill-white" />
          </motion.div>

          {/* Stats badges */}
          <div className="flex-1 flex items-center justify-end gap-2">
            <div className="flex items-center gap-1 bg-white/20 rounded-full px-2.5 py-1">
              <Battery className="w-3.5 h-3.5 text-white" />
              <span className="text-white text-xs font-bold">{batteryLevel ? `${batteryLevel}%` : '--'}</span>
            </div>
            <motion.div
              className="flex items-center gap-1 bg-white/20 rounded-full px-2.5 py-1"
              animate={{ scale: [1, 1.05, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <Zap className="w-3.5 h-3.5 text-white" />
              <span className="text-white text-xs font-bold">{energyConsumed ? `${energyConsumed.toFixed(2)} kWh` : '-- kWh'}</span>
            </motion.div>
          </div>
        </button>
      </motion.div>

      {/* Nav tabs */}
      <div className="relative flex items-stretch justify-around px-2 py-2">
        {renderSlots('charging')}
      </div>
    </motion.nav>
  );
}
